import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext({
    user: null,
    loading: true,
    refresh: () => {},
    logout: async () => {},
    login: async () => {},
    registerProvider: async () => {},
    registerClient: async () => {},
});

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password, userType) => {
        const { data } = await api.post("/auth/login", {
            email,
            password,
            user_type: userType,
        });
        setUser(data);
        return data;
    }, []);

    const registerProvider = useCallback(async (payload) => {
        const { data } = await api.post("/auth/register/provider", payload);
        setUser(data);
        return data;
    }, []);

    const registerClient = useCallback(async (payload) => {
        const { data } = await api.post("/auth/register/client", payload);
        setUser(data);
        return data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* noop */
        }
        setUser(null);
    }, []);

    return (
        <AuthCtx.Provider
            value={{ user, loading, refresh, setUser, logout, login, registerProvider, registerClient }}
        >
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
